import { toast } from "sonner";
import { RotateCcw, Save } from "lucide-react";
import { Button } from "@mcpjam/design-system/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@mcpjam/design-system/dialog";
import { JsonEditor } from "@/components/ui/json-editor";
import type { ProjectHostContextDraft } from "@/lib/client-config";
import { useProjectClientConfigSyncPending } from "@/hooks/use-project-client-config-sync-pending";
import { useHostContextStore } from "@/stores/client-context-store";

interface HostContextDialogProps {
  activeProjectId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaveHostContext?: (
    projectId: string,
    hostContext: ProjectHostContextDraft,
  ) => Promise<void>;
}

export function ClientContextDialog({
  activeProjectId,
  open,
  onOpenChange,
  onSaveHostContext,
}: HostContextDialogProps) {
  const draftHostContext = useHostContextStore((state) => state.draftHostContext);
  const hostContextText = useHostContextStore((state) => state.hostContextText);
  const hostContextError = useHostContextStore((state) => state.hostContextError);
  const isDirty = useHostContextStore((state) => state.isDirty);
  const isSaving = useHostContextStore((state) => state.isSaving);
  const setHostContextText = useHostContextStore(
    (state) => state.setHostContextText,
  );
  const resetToBaseline = useHostContextStore((state) => state.resetToBaseline);
  const failSave = useHostContextStore((state) => state.failSave);
  const syncPending = useProjectClientConfigSyncPending(activeProjectId);

  const handleSave = async () => {
    if (!activeProjectId || !onSaveHostContext || hostContextError) {
      return;
    }

    try {
      await onSaveHostContext(activeProjectId, draftHostContext);
      toast.success("Client context saved.");
      onOpenChange(false);
    } catch {
      failSave();
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !isSaving && !syncPending) {
      resetToBaseline();
    }

    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex h-[80vh] w-[min(96vw,60rem)] max-w-[60rem] flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b px-5 py-4">
          <DialogTitle>Client Context</DialogTitle>
          <DialogDescription>
            Edit the persisted `hostContext` payload used for preview/runtime
            client data.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-3 px-5 py-4">
          <div className="min-h-0 min-w-0 flex-1 overflow-hidden rounded-md border border-border/70 bg-background">
            <JsonEditor
              rawContent={hostContextText}
              onRawChange={setHostContextText}
              mode="edit"
              readOnly={isSaving || syncPending}
              showModeToggle={false}
              className="border-0 bg-background"
              height="100%"
              wrapLongLinesInEdit={false}
              showLineNumbers
              error={hostContextError}
              showValidationErrorInStatusBar={false}
            />
          </div>
        </div>

        <DialogFooter className="border-t px-5 py-4">
          <Button
            variant="outline"
            onClick={resetToBaseline}
            disabled={!isDirty || isSaving || syncPending}
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Reset
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={
              !activeProjectId ||
              !onSaveHostContext ||
              !isDirty ||
              !!hostContextError ||
              isSaving ||
              syncPending
            }
          >
            <Save className="mr-2 h-4 w-4" />
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
